import { api } from "../api/client";
import type { CalendarEvent, CalendarRule, CalendarStatus, NotetakerBot } from "./types";

export function calendarStatus(orgId: string): Promise<CalendarStatus> {
  return api("GET", `/orgs/${orgId}/calendar/status`);
}

/** Where the connect round trip lands afterwards: the Meetings page (default) or Settings. */
export type CalendarReturnTo = "meetings" | "settings";

function returnParam(returnTo: CalendarReturnTo): string {
  return returnTo === "settings" ? "&return=settings" : "";
}

export function calendarStartUrl(
  provider: "google" | "microsoft",
  orgId: string,
  returnTo: CalendarReturnTo = "meetings",
): string {
  return `/api/v1/auth/calendar/${provider}/start?org_id=${encodeURIComponent(orgId)}${returnParam(returnTo)}`;
}

/** F7/§C.2: incremental consent for writing notes into calendar events (separate from the
 * read-only connect above). Starts the provider's write-scope OAuth round trip. */
export function calendarWriteStartUrl(
  provider: "google" | "microsoft",
  orgId: string,
  returnTo: CalendarReturnTo = "meetings",
): string {
  return `/api/v1/auth/calendar/${provider}/write-start?org_id=${encodeURIComponent(orgId)}${returnParam(returnTo)}`;
}

export function stubConnect(orgId: string, provider: "google" | "microsoft"): Promise<{ id: string; status: string }> {
  return api("POST", `/orgs/${orgId}/calendar/stub-connect`, { provider });
}

export function disconnectCalendar(orgId: string, connectionId: string): Promise<void> {
  return api("DELETE", `/orgs/${orgId}/calendar/connections/${connectionId}`);
}

export function listUpcoming(orgId: string): Promise<{ items: CalendarEvent[] }> {
  return api("GET", `/orgs/${orgId}/calendar/events`);
}

export function syncCalendars(orgId: string): Promise<{ items: CalendarEvent[] }> {
  return api("POST", `/orgs/${orgId}/calendar/sync`);
}

export function setMeetingAutoRecord(orgId: string, eventId: string, enabled: boolean): Promise<CalendarRule> {
  return api("POST", `/orgs/${orgId}/calendar/rules`, {
    event_id: eventId,
    scope: "meeting",
    enabled,
  });
}

export function joinNotetaker(
  orgId: string,
  body: { event_id?: string; join_url?: string; title?: string },
): Promise<NotetakerBot> {
  return api("POST", `/orgs/${orgId}/notetaker/join`, body);
}

export function listNotetakers(orgId: string): Promise<{ items: NotetakerBot[] }> {
  return api("GET", `/orgs/${orgId}/notetaker`);
}

export function leaveNotetaker(botId: string): Promise<NotetakerBot> {
  return api("POST", `/notetaker/${botId}/leave`);
}
